import { getMarkdownHeadings } from '@scalar/helpers/markdown/get-markdown-headings';
import { slugger } from '@scalar/helpers/string/slugger';
/**
 * Adds URL-friendly slugs to each heading in the array.
 * Uses GithubSlugger to generate consistent slugs that match GitHub's heading anchor format.
 *
 * @param headings - Array of heading objects containing value and depth
 * @param slugger - GithubSlugger instance for generating consistent slugs
 * @returns Array of headings with added slug property
 *
 * @example
 * const headings = [
 *   { value: 'Getting Started', depth: 1 },
 *   { value: 'Installation', depth: 2 }
 * ]
 * const slugger = new GithubSlugger()
 * withSlugs(headings, slugger)
 * // Returns:
 * // [
 * //   { value: 'Getting Started', depth: 1, slug: 'getting-started' },
 * //   { value: 'Installation', depth: 2, slug: 'installation' }
 * // ]
 */
const withSlugs = (headings, slug) => headings.map((heading) => {
    return {
        ...heading,
        slug: slug(heading.value),
    };
});
/**
 * Extracts all headings from a Markdown string and adds URL-friendly slugs to each heading.
 * Uses GithubSlugger to generate consistent slugs that match GitHub's heading anchor format.
 *
 * @param input - The Markdown string to extract headings from
 * @returns Array of heading objects containing value, depth, and slug
 *
 * @example
 * const markdown = `
 * # Getting Started
 * ## Installation
 * ### Requirements
 * `
 * const headings = getHeadingsFromMarkdown(markdown)
 * // Returns:
 * // [
 * //   { value: 'Getting Started', depth: 1, slug: 'getting-started' },
 * //   { value: 'Installation', depth: 2, slug: 'installation' },
 * //   { value: 'Requirements', depth: 3, slug: 'requirements' }
 * // ]
 */
export function getHeadingsFromMarkdown(input) {
    const { slug } = slugger();
    const headings = getMarkdownHeadings(input);
    return withSlugs(headings, slug);
}
/**
 * Returns the lowest heading level from a list of headings.
 *
 * @param headings - Array of heading objects containing depth property
 * @returns The lowest heading level (1-6) or 1 if no valid headings found
 *
 * @example
 * const headings = [
 *   { value: 'Getting Started', depth: 1 },
 *   { value: 'Installation', depth: 2 }
 * ]
 * getLowestHeadingLevel(headings) // Returns: 1
 */
export const getLowestHeadingLevel = (headings) => {
    const lowestLevel = Math.min(...headings.map((heading) => heading.depth));
    if (lowestLevel >= 1 && lowestLevel <= 6) {
        return lowestLevel;
    }
    return 1;
};
